import { Button, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { useIslandToday, useLocationId } from '../../hooks/useSeedLocation';
import { formatDate, formatMoney } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { type PayoutPayment, PayoutPaymentModal } from './PayoutPaymentModal';

/**
 * Handing one driver his cash, from the screen that says he is owed it.
 *
 * A driver walks up to the bar and asks. The only thing this screen could do about that was "Start
 * this month's payout" — which sweeps every partner into a run, so paying the one person standing
 * in front of you created payout lines for six people who had not asked, each of which somebody now
 * has to settle or explain. Then it sent you to a different tab to find his line among theirs.
 *
 * So the payout is scoped to him and the money is recorded in the same press. The run still exists,
 * because a run is what claims the commission it covers onto one line — without one his balance
 * keeps growing after he has been paid, and a second press would gather the same money again.
 */

const RUNS_PATH = '/admin/v1/referrals/payout-runs';
const LINES_PATH = '/admin/v1/referrals/payout-lines';
const BALANCES_PATH = '/admin/v1/referrals/balances';
const ACCRUALS_PATH = '/admin/v1/referrals/accruals';
/**
 * One row per hand-over, and the only place a hand-over can be taken back.
 *
 * Left off this list, paying a partner recorded the payment and did not put it on the screen that
 * can undo it — the same miss as the supplier side, and it matters for the same reason: the moment
 * anybody wants that row is the moment they have just typed the wrong figure into it.
 */
const PAYMENTS_PATH = '/admin/v1/referrals/payout-payments';

/** Everything a hand-over changes. Kept in one place so neither path below can drift from the other. */
const TOUCHED_BY_A_PAYMENT = [BALANCES_PATH, LINES_PATH, RUNS_PATH, ACCRUALS_PATH, PAYMENTS_PATH];

/**
 * The stretch of trade a partner's next payout covers, and the morning it is handed over.
 *
 * Not a schedule. It is the period a volume threshold is counted inside, which is why it belongs
 * beside the figure: crossing a threshold applies the better rate to everything in the period, so
 * the amount and the window it was struck over are one answer. Nothing runs on `payoutDate`.
 */
export interface PayoutPeriod {
  cadence: string;
  periodStart: string;
  periodEnd: string;
  payoutDate: string;
}

export interface ReferralBalance {
  profileId: string;
  profileName: string;
  accruedMinor: number;
  currency: string;
  preferredPayoutType: string;
  payable: boolean;
  shortfallMinor?: number | null;
  /** Null where nobody has agreed a cadence with them — never a guess. */
  period?: PayoutPeriod | null;
}

interface PayoutLine {
  id: string;
  profileId: string;
  currency: string;
  amountMinor: number;
  /** What has already been counted out against it. A fresh line is always zero. */
  paidMinor?: number;
}

interface Accrual {
  at?: string;
}

export function PayPartnerButton({
  balance,
  alsoOwed = [],
  label = 'Pay them',
  size = 'xs',
}: {
  balance: ReferralBalance;
  /** What the same partner is owed in OTHER currencies — see the note in the modal. */
  alsoOwed?: ReferralBalance[];
  label?: string;
  size?: 'xs' | 'sm';
}) {
  const locationId = useLocationId();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  /**
   * The commission actually being handed over, so the payout records the period it really covers.
   *
   * The month-to-date guess the bulk button uses is a label on a run that may well be closing
   * commission from March. Asked here because the answer is one small request and this run exists
   * to be read back later by somebody asking what a driver was paid for.
   */
  const unpaid = useQuery({
    queryKey: listKey(ACCRUALS_PATH, { profileId: balance.profileId, unpaidOnly: 'true' }),
    queryFn: () =>
      rawApi<{ rows: Accrual[] }>('GET', `${ACCRUALS_PATH}?profileId=${balance.profileId}&unpaidOnly=true`),
    enabled: open,
  });
  const earliest = (unpaid.data?.rows ?? [])
    .map((a) => (a.at ?? '').slice(0, 10))
    .filter(Boolean)
    .sort()[0];
  // The BAR's calendar, not the reader's browser: `useIslandToday` carries the reasoning and the
  // six times a UTC-sliced date has answered tomorrow from six in the evening.
  const today = useIslandToday();
  /**
   * The period still running when this hand-over lands, or null — see the note in the modal. A
   * partner nobody has agreed a period with has no middle to be caught in.
   */
  const midPeriod = balance.period && today < balance.period.periodEnd ? balance.period : null;

  const submit = async (payment: PayoutPayment) => {
    if (!locationId) return;
    setSaving(true);
    try {
      const run = await rawApi<{ lines: PayoutLine[] }>('POST', RUNS_PATH, {
        locationId,
        periodStart: earliest ?? today,
        periodEnd: today,
        profileId: balance.profileId,
      });
      // One line per currency, because a partner earns on dollar bills and lempira bills alike.
      // Only the one the reader is counting out is settled here; anything else he is owed stays
      // owed and appears on the payouts list rather than being quietly closed.
      //
      // A run writes NO line for money already sitting on a payout that has taken a part payment —
      // that commission is spoken for and re-gathering it would pay it twice. What is still owed on
      // that line is owed all the same, so the fallback is to settle the rest of it rather than to
      // announce that a partner with a balance on screen has nothing coming.
      let line = run.lines.find((l) => l.currency === balance.currency);
      if (!line) {
        const waiting = await rawApi<PayoutLine[]>('GET', `${LINES_PATH}?profileId=${balance.profileId}`);
        line = waiting.find((l) => l.currency === balance.currency);
      }
      if (!line) {
        // The figure on the row was already stale — somebody settled this commission between the
        // page loading and this press. Said as what it is rather than as a failure, because nothing
        // went wrong and no money is missing; the list is simply out of date.
        notifyDone(
          'Nothing left to hand over',
          `${balance.profileName}'s commission in this money has already been paid. The list has been brought up to date.`,
        );
        setOpen(false);
        for (const path of TOUCHED_BY_A_PAYMENT) {
          void queryClient.invalidateQueries({ queryKey: listKey(path) });
        }
        return;
      }
      // What is left on the line, not what the line was for: one already part paid owes the rest.
      const remaining = line.amountMinor - (line.paidMinor ?? 0);
      await rawApi('POST', `${LINES_PATH}/${line.id}/pay`, payment);
      notifyDone(
        'Payment recorded',
        payment.amountMinor >= remaining
          ? `${balance.profileName} is settled up to today.`
          : `${formatMoney(remaining - payment.amountMinor, balance.currency)} of this is still outstanding, on the Payouts tab.`,
      );
      setOpen(false);
      for (const path of TOUCHED_BY_A_PAYMENT) {
        void queryClient.invalidateQueries({ queryKey: listKey(path) });
      }
    } catch (err) {
      // Named rather than generic, because the run may already exist by the time the payment
      // fails: the line is waiting on the Payouts tab and pressing this again would make a second.
      notifyError(err, `record a payment to ${balance.profileName}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button size={size} variant="light" onClick={() => setOpen(true)} disabled={!locationId}>
        {label}
      </Button>
      {open && (
        <PayoutPaymentModal
          opened
          onClose={() => setOpen(false)}
          title={`Pay ${balance.profileName}`}
          outstandingMinor={balance.accruedMinor}
          currency={balance.currency}
          belowThreshold={!balance.payable}
          defaultMethod={balance.preferredPayoutType}
          submitting={saving}
          onSubmit={(payment) => void submit(payment)}
        >
          <Text size="sm" c="dimmed">
            {earliest && earliest !== today
              ? `Commission earned since ${formatDate(earliest)} and not yet handed over.`
              : 'Commission earned and not yet handed over.'}
          </Text>
          {midPeriod && (
            /*
             * SAID, BECAUSE THE ROW BEHIND THIS MODAL ALREADY SAYS THE OTHER THING.
             *
             * The Period column one cell over reads "07 Aug – 13 Aug", and this button gathers
             * everything up to TODAY. Pressed on the Wednesday, the tabs it gathers are claimed onto
             * a payout line and frozen at the rate they carry — so if Thursday's trade then takes
             * the week past a threshold, Thursday is lifted and the tabs already handed over are
             * not. That is the frozen fence working as designed, and it is money in his hand that
             * cannot be restated; but "the higher rate applies to everything" is what the owner
             * agreed with the partner, so the gap has to be visible BEFORE the press rather than
             * discovered when the partner asks about it.
             *
             * Nothing here refuses the press. Paying a driver who walks up mid-week is the normal
             * thing to do, and topping him up afterwards is an adjustment the owner makes himself.
             */
            <Text size="sm" c="dimmed">
              This settles in the middle of their period, which runs to {formatDate(midPeriod.periodEnd)}.
              Whatever they earn between now and then will not raise the rate on what you are handing over
              here.
            </Text>
          )}
          {alsoOwed.length > 0 && (
            // Said, not silently done. A partner earns on dollar bills and lempira bills alike and
            // they are separate debts — counting out one of them must not read as settling both.
            <Text size="sm" c="dimmed">
              {balance.profileName} is also owed{' '}
              {alsoOwed.map((b) => formatMoney(b.accruedMinor, b.currency)).join(' and ')}. That is a separate
              handover and stays owed.
            </Text>
          )}
        </PayoutPaymentModal>
      )}
    </>
  );
}
